import PropTypes from 'prop-types';
import { path } from 'ramda';
import React, { Component } from 'react';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { connect } from 'react-redux';
import { createResource } from 'redux-json-api';
import { SubmissionError } from 'redux-form';

import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';

import { closeLeftBarModule, showSnackbarMessage } from '../../modules/actions/layout';
import ApiUserForm from './Form';

const styles = {
  column: {
    margin: 20,
    width: '100%',
  },
};

class ApiUserAdd extends Component {
  static propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    createResource: PropTypes.func.isRequired,
    closeLeftBarModule: PropTypes.func.isRequired,
    showSnackbarMessage: PropTypes.func.isRequired,
  };

  /**
   * Component state variables.
   *
   * - `apiUserId`: records the id of the ApiUser if submission was succesful.
   *   Used when tracking errors adding ApiRoles to an ApiUser when adding that
   *   ApiUser was succesfull.
   */
  state = { apiUserId: null };

  render() {
    const { apiEndpoint, createResource, closeLeftBarModule, showSnackbarMessage } = this.props;

    return (
      <Grid fluid>
        <Row>
          <Col style={styles.column}>
            <CardHeader title="Gebruiker toevoegen" />

            <Divider />

            <ApiUserForm
              apiUserFieldsDisabled={this.state.apiUserId !== null}
              onSubmit={async (values) => {
                let apiUser;
                let apiUserId;

                /**
                 * First, try creating the user.
                 */
                if (!this.state.apiUserId) {
                  try {
                    const apiUserResource = {
                      type: 'api-user',
                      attributes: {
                        'email': values.email,
                        'first-name': values.first_name,
                        'last-name': values.last_name,
                        'phone': values.phone,
                        'password': values.password,
                        'text-notification': values.text_notification,
                      },
                    };

                    /**
                     * If the default-company is present, set it.
                     */
                    if (values.default_company)
                      apiUserResource.attributes['default-company'] = `${apiEndpoint}/company/${values.default_company}`;

                    apiUser = await createResource(apiUserResource);
                  } catch (exc) {
                    let errors = {};
                    const res = await exc.response.json();

                    res.errors.forEach(e => {
                      const field = path(['source', 'pointer'], e)
                        .replace('/data/attributes/', '')
                        .replace('-', '_');

                      if (field === 'non_field_errors') errors._error = e.detail;
                      else {
                        errors[field] = e.detail;

                        switch (field) {
                          case 'email': {
                            errors[field] += ' (voorbeeld: h.devries@arrivatouring.nl)';
                            break;
                          }
                          case 'first_name': {
                            errors[field] += ' (voorbeeld: Henk)';
                            break;
                          }
                          case 'last_name': {
                            errors[field] += ' (voorbeeld: de Vries)';
                            break;
                          }
                          case 'phone': {
                            errors[field] += ' (voorbeeld: 0505260268)';
                            break;
                          }
                        }
                      }
                    });

                    showSnackbarMessage('Er is iets fout gegaan bij het toevoegen van de gebruiker.');

                    throw new SubmissionError(errors);
                  }

                  apiUserId = path(['data', 'id'], apiUser);
                } else {
                  apiUserId = this.state.apiUserId;
                }

                /**
                 * Then, try to create the role(s).
                 */
                if (values.roles) {
                  const roleErrors = [];

                  for (let i = 0; i < values.roles.length; i++) {
                    const role = values.roles[i];

                    try {
                      if (Number(role.id) === -1) {
                        await createResource({
                          type: 'api-role',
                          attributes: {
                            'role': role.role,
                            'user': `${apiEndpoint}/api-user/${apiUserId}`,
                            'company': `${apiEndpoint}/company/${role.company}`,
                          },
                        });
                      }
                    } catch (exc) {
                      const res = await exc.response.json();

                      res.errors.forEach(e => {
                        if (e.role) roleErrors.push(e.role);
                        else if (e.user) roleErrors.push(e.user);
                        else if (e.detail) roleErrors.push(e.detail);
                        else roleErrors.push(JSON.stringify(e));
                      });
                    }
                  };

                  if (roleErrors.length > 0) {
                    this.setState({ apiUserId });
                    showSnackbarMessage('Er is iets fout gegaan bij het toevoegen van de rollen.');
                    throw new SubmissionError({ roles: roleErrors.join('\n') });
                  }
                }

                showSnackbarMessage('Gebruiker toegevoegd');
                closeLeftBarModule('apiUser');
              }}
            />
          </Col>
        </Row>
      </Grid>
    );
  };
}

const mapStateToProps = (state) => ({
  apiEndpoint: path(['api', 'endpoint', 'axiosConfig', 'baseURL'], state),
});

const mapDispatchToProps = {
  createResource,
  closeLeftBarModule,
  showSnackbarMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiUserAdd);
